import {
  FormControl,
  FormErrorMessage,
  Flex,
  Switch,
  FormLabel,
  forwardRef,
} from "@chakra-ui/react";
import { FormProvider, useForm } from "react-hook-form";
import Input from "../inputs/Input";
import Select from "../inputs/Select";
import { useEffect, useImperativeHandle } from "react";
import _ from "lodash";
import { TicketLabel, TicketPriority } from "../../types/ticket.types";

type CreateLabelForm = {
  defaultValue: TicketLabel | null;
};

export type CreateLabelFormHandler = {
  values: () => TicketLabel;
  isValid: () => boolean;
  isDirty: () => boolean;
  reset: () => void;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CreateLabelForm = forwardRef(({ defaultValue }, ref) => {
  const methods = useForm();
  useEffect(() => {
    if (defaultValue) {
      methods.reset({
        label: defaultValue.label,
        discount: defaultValue.discount,
        priority: defaultValue.priority,
        proof: defaultValue.proof,
      });
    } else {
      methods.reset({
        label: "",
        discount: 0,
        priority: TicketPriority.LOW,
        proof: false,
      });
    }
  }, [methods, defaultValue]);
  useImperativeHandle(ref, () => ({
    values: () => methods.getValues(),
    isValid: () => methods.trigger(),
    reset: () =>
      methods.reset({
        label: "",
        discount: 0,
        priority: TicketPriority.LOW,
        proof: false,
      }),
    isDirty: () => {
      return !_.isEqual(methods.getValues(), {
        label: "",
        discount: 0,
        priority: TicketPriority.LOW,
        proof: false,
      });
    },
  }));
  return (
    <FormProvider {...methods}>
      <form style={{ marginTop: "32px", marginBottom: "32px" }}>
        <Flex direction="column" gap="24px">
          <FormControl isInvalid={!!methods.formState.errors.label}>
            <Input
              id="label"
              defaultValue={defaultValue?.label ?? undefined}
              placeholder="Nom du motif"
              {...methods.register("label", {
                required: "Ce champ est obligatoire",
              })}
            ></Input>
            <FormErrorMessage>
              <>{methods.formState.errors?.label?.message}</>
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!methods.formState.errors.discount}>
            <Input
              id="discount"
              placeholder="Remise (%)"
              defaultValue={0}
              type="number"
              step={1}
              min={0}
              max={100}
              {...methods.register("discount", {
                required: "Ce champ est obligatoire",
                min: 0,
                max: 100,
                valueAsNumber: true,
              })}
            ></Input>
            <FormErrorMessage>
              <>{methods.formState.errors?.discount?.message}</>
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!methods.formState.errors.priority}>
            <Select
              id="priority"
              placeholder="Priorité"
              defaultValue={"LOW"}
              {...methods.register("priority", {
                required: "Ce champ est obligatoire",
              })}
            >
              <option value="LOW">Normal</option>
              <option value="MEDIUM">Moyenne</option>
              <option value="HIGH">Haute</option>
            </Select>
            <FormErrorMessage>
              <>{methods.formState.errors?.priority?.message}</>
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!methods.formState.errors.proof}>
            <Switch
              id="proof"
              placeholder="Preuve"
              colorScheme="brand"
              size="lg"
              {...methods.register("proof")}
              mr={4}
            ></Switch>
            <FormLabel htmlFor="proof" mb="0" display={"inline"}>
              Demander une photo
            </FormLabel>
            <FormErrorMessage>
              <>{methods.formState.errors?.proof?.message}</>
            </FormErrorMessage>
          </FormControl>
        </Flex>
      </form>
    </FormProvider>
  );
});

export default CreateLabelForm;
